/**
 * Calculator Actions
 *
 * Footer row with action buttons:
 * - Create vehicle draft OR Open existing vehicle
 * - Create purchase invoice (navigates to Add Purchase Invoice with prefill)
 * - Copy results to clipboard
 * - Close calculator
 */

import { useTranslation } from 'react-i18next';
import { Copy, X, Car, FileText, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CalculatorActionsProps {
  hasResults: boolean;
  isLoading: boolean;
  hasExistingVehicle: boolean;
  onCreateVehicle: () => void;
  onOpenExistingVehicle: () => void;
  onCreatePurchaseInvoice: () => void;
  onCopy: () => void;
  onClose: () => void;
  className?: string;
}

export function CalculatorActions({
  hasResults,
  isLoading,
  hasExistingVehicle,
  onCreateVehicle,
  onOpenExistingVehicle,
  onCreatePurchaseInvoice,
  onCopy,
  onClose,
  className,
}: CalculatorActionsProps) {
  const { t } = useTranslation('common');

  // Actions are enabled when we have results and not loading
  const actionsEnabled = hasResults && !isLoading;

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-border',
        className
      )}
    >
      {/* Primary actions: Create vehicle/invoice - stack on mobile */}
      <div className="flex flex-col sm:flex-row gap-2">
        {hasExistingVehicle ? (
          <Button
            variant="outline"
            size="sm"
            disabled={!actionsEnabled}
            onClick={onOpenExistingVehicle}
            className="justify-start sm:justify-center"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {t('calculator.openVehicle', 'Open voertuig')}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={!actionsEnabled}
            onClick={onCreateVehicle}
            className="justify-start sm:justify-center"
          >
            <Car className="h-4 w-4 mr-2" />
            {t('calculator.createVehicle', 'Voertuig aanmaken')}
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          disabled={!actionsEnabled}
          onClick={onCreatePurchaseInvoice}
          className="justify-start sm:justify-center"
        >
          <FileText className="h-4 w-4 mr-2" />
          {t('calculator.createInvoice', 'Inkoopfactuur')}
        </Button>
      </div>

      {/* Secondary actions: Copy and Close - always row, right-aligned */}
      <div className="flex items-center gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCopy} disabled={!hasResults}>
          <Copy className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">{t('calculator.copyResults', 'Kopieer')}</span>
        </Button>

        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">{t('calculator.close', 'Sluiten')}</span>
        </Button>
      </div>
    </div>
  );
}
